import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "city", "state", "lat", "lng", "distanceSelect"]
  static values = {
    types: { type: String, default: "(cities)" },
    country: { type: String, default: "us" },
    // CSS selector for a <select> whose `option[value="distance"]` should be
    // toggled in sync with geo availability. Optional — defaults to no-op.
    distanceSortSelectSelector: { type: String, default: "" }
  }

  connect() {
    this.syncDistanceSelect()

    if (window.google?.maps) {
      this.initializeAutocomplete()
    } else {
      this.boundInit = () => this.initializeAutocomplete()
      window.addEventListener('google-maps-loaded', this.boundInit)
    }
  }

  disconnect() {
    if (this.boundInit) {
      window.removeEventListener('google-maps-loaded', this.boundInit)
    }
  }

  initializeAutocomplete() {
    const input = this.inputTarget

    this.autocomplete = new google.maps.places.Autocomplete(input, {
      types: [this.typesValue],
      componentRestrictions: { country: this.countryValue },
      fields: ['address_components', 'geometry']
    })

    this.autocomplete.addListener('place_changed', () => {
      this.handlePlaceChanged()
    })

    // Prevent Enter from submitting the form while autocomplete is open
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()
      }
    })

    // Clear hidden fields when user empties the input
    input.addEventListener('blur', () => {
      setTimeout(() => {
        if (!input.value.trim()) {
          this.clearFields()
        }
      }, 200)
    })
  }

  handlePlaceChanged() {
    const place = this.autocomplete.getPlace()

    if (!place.geometry) {
      this.clearFields()
      return
    }

    const components = {}
    place.address_components?.forEach(component => {
      const type = component.types[0]
      components[type] = component.long_name
      if (type === 'administrative_area_level_1') {
        components[type + '_short'] = component.short_name
      }
    })

    const city = components.locality ||
                 components.sublocality ||
                 components.administrative_area_level_2 || ''
    const state = components.administrative_area_level_1_short || ''
    const lat = place.geometry.location.lat()
    const lng = place.geometry.location.lng()

    this.cityTarget.value = city
    this.stateTarget.value = state
    this.latTarget.value = lat
    this.lngTarget.value = lng

    this.inputTarget.value = [city, state].filter(Boolean).join(', ')
    this.syncDistanceSelect()
  }

  clearFields() {
    this.cityTarget.value = ''
    this.stateTarget.value = ''
    this.latTarget.value = ''
    this.lngTarget.value = ''
    if (this.hasDistanceSelectTarget) {
      this.distanceSelectTarget.value = ''
    }
    this.syncDistanceSelect()
  }

  syncDistanceSelect() {
    const hasGeo = this.hasLatTarget && this.latTarget.value.trim().length > 0

    if (this.hasDistanceSelectTarget) {
      this.distanceSelectTarget.disabled = !hasGeo
    }

    this.syncDistanceSortOption(hasGeo)
  }

  syncDistanceSortOption(hasGeo) {
    const selector = this.distanceSortSelectSelectorValue
    if (!selector) return

    const sortSelect = document.querySelector(selector)
    if (!sortSelect) return

    const distanceOption = sortSelect.querySelector('option[value="distance"]')
    if (!distanceOption) return

    distanceOption.disabled = !hasGeo

    // If the selected sort was "distance" and we're disabling it, fall back
    // to "name" and notify any listeners so the filter form re-submits.
    if (!hasGeo && sortSelect.value === 'distance') {
      sortSelect.value = 'name'
      sortSelect.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }
}
